import BasicHttpClient from '@common/clients/http/basic-http.client.impl';
import { HttpRequestOptions } from '@common/clients/http/http.client.interface';
import { CommonError } from '@common/models/error.model';
import { AUTH_SERVICE, IAuthService } from '@common/services/auth/auth.service.interface';
import { getErrorFromHttpRawResponse } from '@common/utils/error.util';
import appStore from '@scoring-web/app/store/store.app';
import envConfig from '@scoring-web/configs/env/env.config';
import { i18n } from '@scoring-web/configs/locale/locale.config';
import userStore from '@scoring-web/modules/user/stores/user.store';
import { container } from 'tsyringe';

export default class HttpClient extends BasicHttpClient {
  authService: IAuthService;

  constructor() {
    super();
    this.baseUrl = envConfig.apiBaseUrl;
    this.authService = container.resolve(AUTH_SERVICE);
  }

  // eslint-disable-next-line class-methods-use-this
  getDefaultHeaders(): Record<string, string> {
    const { token } = userStore.state;

    return {
      ...super.getDefaultHeaders(),
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async handleResponse<Type>(response: Response): Promise<Type> {
    const data = await response.json();
    const error = getErrorFromHttpRawResponse(data);
    if (error instanceof CommonError) throw error;

    return data;
  }

  // eslint-disable-next-line class-methods-use-this
  async handleError<Type>(
    error: unknown,
    url: string,
    options: HttpRequestOptions<Type>,
  ): Promise<Type> {
    const request = { options, url };
    let errorObject = new CommonError<Type>({
      message: i18n.t('error.generic.description'),
      messageTranslationKey: 'error.generic.description',
      ...getErrorFromHttpRawResponse(error),
      request,
    });

    if (errorObject.statusCode === 401 || errorObject.statusCode === 403) {
      const token = await this.authService.getToken();

      if (token) {
        userStore.actions.updateToken(token);
        return this.makeRequest<Type>(url, options);
      }

      // Set the error before resetting the store
      errorObject = new CommonError<Type>({
        ...errorObject,
        message: i18n.t('error.auth-expiration'),
        messageTranslationKey: 'error.auth-expiration',
        request,
      });

      await this.authService.logout();
      appStore.actions.reset();
    }

    throw errorObject;
  }
}
